import { AnyString, CustomAppEvents } from '@packages/shared/core/common/entities';
import { Logger } from '@packages/shared/core/common/logger/interfaces';
import { CookieConsentState } from '@packages/shared/core/cookie-consent/entities';
import { AnalyticsService } from '@packages/shared/core/analytics/services';
import { CookieConsentService } from '@packages/shared/core/cookie-consent/interfaces';
import { checkIsClientSide } from '@packages/shared/core/common/utils/checkIsClientSide';
import { normalizeError } from '@packages/shared/core/common/utils/errors';
import { ErrorMonitoringService } from '@packages/shared/core/common/services/errorMonitoring';
import { notAvailable } from '@packages/shared/core/common/constants';
import { HeatmapRecordingService } from './ClarityHeatmapRecordingService';

export type HeatmapRecordingIdentifyUserOptions = {
  customId: string;
  customSessionId?: string;
  customPageId?: string;
  friendlyName?: string;
};

export type HeatmapRecordingCustomTagUnion = 'email' | 'experiments' | 'user id' | AnyString;

export class ClarityHeatmapRecordingClientService implements HeatmapRecordingService {
  private readonly logger: Logger;
  private readonly analyticsService: AnalyticsService;
  private readonly cookieConsentService: CookieConsentService;
  private readonly errorMonitoringService: ErrorMonitoringService;
  private initialized = false;

  constructor(
    logger: Logger,
    analyticsService: AnalyticsService,
    cookieConsentService: CookieConsentService,
    errorMonitoringService: ErrorMonitoringService,
  ) {
    this.logger = logger;
    this.analyticsService = analyticsService;
    this.cookieConsentService = cookieConsentService;
    this.errorMonitoringService = errorMonitoringService;

    const onConsentChange = (consentState: CookieConsentState) => {
      if (consentState.TARGETING) {
        this.giveCookieConsent();
      }
    };
    this.cookieConsentService.addConsentChangeListener(onConsentChange);
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (this.clarity) {
      await this.startRecording();
      this.initialized = true;
      return;
    }

    document.addEventListener(
      CustomAppEvents.CLARITY_LOADED,
      async () => {
        await this.startRecording();
        this.initialized = true;
      },
      { once: true },
    );
  }

  async startRecording() {
    try {
      if (this.cookieConsentService.isInitialized()) {
        this.setInitialConsentState();
      } else {
        this.deferSetInitialConsentState();
      }
      this.triggerCustomEvent('asknebula_quiz');

      const deviceId = await this.analyticsService.getDeviceId();
      await this.identifyUser({ customId: deviceId });
      if (deviceId) this.addCustomTag('device id', deviceId);
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error('Failed to start clarity recording', {
        error: error.message,
      });

      this.errorMonitoringService.captureError(error, {
        tags: {
          get_params: window?.location?.search || notAvailable,
          error_message: error.message,
          error_reason: 'start_recording_clarity',
        },
      });
    }
  }

  triggerCustomEvent(value: string) {
    this.track('event', value);
  }

  addCustomTag(key: HeatmapRecordingCustomTagUnion, value: string | [string, string]) {
    this.triggerCustomEvent(`Tag: ${key}`);
    this.track('set', key, value);
  }

  markImportantSession(reason: string) {
    this.track('upgrade', reason);
  }

  giveCookieConsent() {
    this.track('consent');
  }

  revokeCookieConsent() {
    this.track('consent', false);
  }

  private get clarity() {
    if (!checkIsClientSide()) {
      return;
    }

    return window.clarity;
  }

  private track(
    command: 'identify',
    customId: string,
    customSessionId?: string,
    customPageId?: string,
    friendlyName?: string,
  ): Promise<void>;
  private track(command: 'consent', given?: boolean): void;
  private track(command: 'event', value: string): void;
  private track(command: 'set', key: HeatmapRecordingCustomTagUnion, value: string | [string, string]): void;
  private track(command: 'upgrade', reason: string): void;

  private track(command: string, ...args: Array<unknown>): unknown {
    if (!this.clarity) {
      return;
    }

    return this.clarity(command, ...args);
  }

  private async identifyUser(options: HeatmapRecordingIdentifyUserOptions) {
    try {
      await this.track(
        'identify',
        options.customId,
        options.customSessionId,
        options.customPageId,
        options.friendlyName,
      );
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error('Failed to identify user for clarity', {
        error: error.message,
      });

      this.errorMonitoringService.captureError(error, {
        tags: {
          get_params: window?.location?.search || notAvailable,
          error_message: error.message,
          error_reason: 'identify_user_clarity',
        },
      });
    }
  }

  private deferSetInitialConsentState(): void {
    document.addEventListener(
      CustomAppEvents.ONE_TRUST_LOADED,
      () => {
        this.setInitialConsentState();
      },
      { once: true },
    );
  }

  private setInitialConsentState(): void {
    if (this.cookieConsentService.isTargetingConsentGiven()) {
      this.giveCookieConsent();
    }
  }
}
